import { IVec3, Vec3 } from "@base/math/Vec3";
import { NormalizedRecord } from "@base/util/NormalizedRecord";
import { GetTaggedType, PropertyNamesWithType, TupleToUnion } from "@base/util/TypeUtil";
import { ILayerData, LockState } from "@biz/definitions/LayerDefinitions";
import { ILoungeData } from "@biz/definitions/LoungeDefinitions";
import { IModelData } from "@biz/definitions/ModelDefinitions";
import { IModelProductData } from "@biz/definitions/ModelProductDefinitions";
import { IGroupData } from "@biz/definitions/ProductDefinitions";
import { MonthShort } from "@biz/definitions/SceneDefinitions";
import { SelectionData, SelectionType } from "@biz/definitions/SelectionDefinitions";
import { ISunSailData } from "@biz/definitions/SunSailDefinitions";
import { ICurtainData } from "@biz/items/curtain/CurtainDefinitions";
import { IFenceData } from "@biz/items/fence/FenceDefinitions";
import { IGroundData } from "@biz/items/ground/GroundDefinitions";
import { INetData } from "@biz/items/net/NetDefinitions";
import { IOpeningData } from "@biz/items/opening/OpeningDefinitions";
import { IRaffData } from "@biz/items/raff/RaffDefinitions";
import { IRoofData } from "@biz/items/roof/RoofDefinitions";
import { ISandboxData } from "@biz/items/sandbox/SandboxDefinitions";
import { IWallData } from "@biz/items/wall/WallDefinitions";
import { IVariantDataCommon } from "@biz/userProjects/new/IVariant";

export const shadowTypes = ["basic", "pcf", "pcfSoft", "vsm"] as const;
export type ShadowType = TupleToUnion<typeof shadowTypes>;
export type ShadowMode = "on" | "off" | "timelapse";
export type AnimationMode = "on" | "off" | "paused";
export type ViewMode = "2d" | "3d";
export type ConfiguratorMode = "2d" | "3d";


// todo: new product
export type ProductData3d = ISunSailData | IRaffData | ICurtainData | ILoungeData | IModelProductData | INetData;
export type SourroundingData = IModelData | IGroundData | IWallData | IFenceData | IRoofData | IOpeningData | ISandboxData;
export type ConfiguratorItemData = ProductData3d | SourroundingData | IGroupData;

export function expect3d<T extends ConfiguratorItemData["itemType"]>(product: ConfiguratorItemData, itemType: T): asserts product is GetTaggedType<ConfiguratorItemData, { itemType: T }> {
  if (product.itemType !== itemType)
    throw new Error("Expected 3d product " + itemType + " but got " + product.itemType)
}

export const defaultDebugProperties = {
  thumbCreationMode: false,
  showBoundingBoxes: false,
  showLightHelper: false,
  showShadowMapHelper: false,
  sailDebug: {
    mountPoints: false,
    netTriangle: false,
    halfVector: false,
    approximation: false,
  },
  poleDebug: {
    angle: false,
    heightChangeSlide: false,
    coilUp: false
  },
};

export interface ISkyParams {
  turbidity: number;
  rayleigh: number;
  mieCoefficient: number;
  mieDirectionalG: number;
}

export interface ISkySettings {
  use: "morning" | "noon" | "blend";
  blendFrom: number;
  blendDuration: number;
  morning: ISkyParams;
  noon: ISkyParams;
}

export interface ISunSettings {
  lightColor: string,
  lightIntensity: number
}

export interface IPerspectiveCameraState {
  fov: number;
  near: number;
  far: number;
  position: IVec3;
  target: IVec3;
  zoom: number;
}

export interface IOrthoCameraState {
  near: number;
  far: number;
  position: IVec3;
  zoom: number;
}


export const DEFAULT_GRID_SIZE = 50;
export interface ISceneSettings {
  viewMode: ViewMode;
  showGrid: boolean;
  gridSize?: number;
  perspectiveCamera: IPerspectiveCameraState;
  orthoCamera: IOrthoCameraState;
  presentationMode?: {
    settings: Pick<ISceneSettings, "shadowMode" | "animationMode" | "showGrid" | "hideErrors">,
    products: LockState | undefined;
    surroundings: LockState | undefined;
  };
  shadowMode: ShadowMode;
  animationMode: AnimationMode;
  hideErrors?: boolean;
  sun: ISunSettings;
  sky: ISkySettings;
  month: MonthShort,
  dayTime: number;
  cardinalDirection: number;
  latitude: number;
  longitude: number;
  utcOffset: number;
  measureFontSize: number;
}

export function getInitialPerspectiveCamera(): IPerspectiveCameraState {
  return {
    fov: 45,
    near: 0.1,
    far: 200,
    target: Vec3.from(0, 0, 0),
    position: Vec3.from(8, 17, 15),
    zoom: 1
  }
}

export function getInitialOrthoCamera(): IOrthoCameraState {
  return {
    near: 0.1,
    far: 200,
    position: Vec3.from(0, 100, 0),
    zoom: 50
  }
}

export function getInitialSunSettings(): ISunSettings {
  return {
    lightColor: "#ffffff",
    lightIntensity: 1.0,
  }
}

export function getInitialSkySettings(): ISkySettings {
  return {
    use: "blend",
    blendFrom: 0.08,
    blendDuration: 0.1,
    morning: {
      /*turbidity: 8.44,
      rayleigh: 0.8,
      mieCoefficient: 0.0327,
      mieDirectionalG: 0.9999,*/
      // turbidity: 1.5583,
      // rayleigh: 2.7,
      // mieCoefficient: 0.0617,
      // mieDirectionalG: 0.9999
      turbidity: 0.01,
      rayleigh: 1.6,
      mieCoefficient: 0.1,
      mieDirectionalG: 0.9999,
    },
    noon: {
      /*turbidity: 3.6,
      rayleigh: 0.6,
      mieCoefficient: 0.04,
      mieDirectionalG: 0.9999,*/
      turbidity: 1.0,
      rayleigh: 1.2,
      mieCoefficient: 0.1,
      mieDirectionalG: 0.9999,
    },
  }
}

export const defaultSceneSettings: ISceneSettings = {
  viewMode: "3d",
  showGrid: true,
  perspectiveCamera: getInitialPerspectiveCamera(),
  orthoCamera: getInitialOrthoCamera(),
  shadowMode: "on",
  animationMode: "off",
  sun: getInitialSunSettings(),
  sky: getInitialSkySettings(),
  month: MonthShort.Aug,
  dayTime: 13,
  cardinalDirection: 0,
  latitude: 48.0486281,
  longitude: 13.764135,
  utcOffset: -new Date().getTimezoneOffset() / 60,
  measureFontSize: 12.5
};
export interface IVariantData3d extends IVariantDataCommon {
  type: "sail3d";
  sunSails: NormalizedRecord<string, ISunSailData>;
  raffs: NormalizedRecord<string, IRaffData>;
  models: NormalizedRecord<string, IModelData>;
  modelProducts: NormalizedRecord<string, IModelProductData>;
  grounds: NormalizedRecord<string, IGroundData>;
  roofs: NormalizedRecord<string, IRoofData>;
  openings: NormalizedRecord<string, IOpeningData>;
  walls: NormalizedRecord<string, IWallData>;
  fences: NormalizedRecord<string, IFenceData>;
  sandboxes: NormalizedRecord<string, ISandboxData>;
  curtains: NormalizedRecord<string, ICurtainData>;
  lounges: NormalizedRecord<string, ILoungeData>;
  items: NormalizedRecord<string, ConfiguratorItemData>;
  nets: NormalizedRecord<string, INetData>;
  groups: NormalizedRecord<string, IGroupData>;
  layers: Record<string, ILayerData>;
  debugFlags: typeof defaultDebugProperties;
  settings: ISceneSettings;
  selection?: SelectionData | SelectionData[];
  // panel?: { open: boolean } // ??
  sideTab: string;
  testSettings?: { selectedRopeColor?: string }
}

export function getItemRecordForItem(item: ConfiguratorItemData): ItemRecord | undefined {
  return getItemRecordForItemType(item.itemType);
}

export function getItemRecordForItemType(itemType: ConfiguratorItemData["itemType"]): ItemRecord | undefined {
  const info = getSelectionTypeInfo(itemType);
  if (!info) {
    console.error("Failed to get selection type info: ", itemType)
    return undefined;
  }
  return info.data;
}

function getSelectionTypeInfo(selectionType: SelectionType): { data: ItemRecord } {
  return selectionTypeInfos[selectionType];
}

// todo: new product
export const selectionTypeInfos: Record<SelectionType, { data: ItemRecord }> = {
  sunSail: { data: "sunSails" },
  raff: { data: "raffs" },
  curtain: { data: "curtains" },
  lounge: { data: "lounges" },
  modelProduct: { data: "modelProducts" },
  model: { data: "models" },
  wall: { data: "walls" },
  ground: { data: "grounds" },
  fence: { data: "fences" },
  sandbox: { data: "sandboxes" },
  roof: { data: "roofs" },
  opening: { data: "openings" },
  net: { data: "nets" },
  group: { data: "groups" }
}

export const productTypes: ProductData3d["itemType"][] = ["sunSail", "raff", "curtain", "lounge", "modelProduct", "net"];
export const productRecords = productTypes.map((p) => getItemRecordForItemType(p)).filter((p): p is ItemRecord => !!p)
export type ItemRecord = PropertyNamesWithType<IVariantData3d, NormalizedRecord<any, any>>;